import { useAnnotationTool } from "@/stores/annotation-tool";

import { hasSeenPen, msSincePen, PALM_WINDOW_MS } from "./stylus-state";

/** The reader's single input policy (plan north-star B).
 *
 * ONE expression of who owns a contact, consumed by BOTH the scroller-level
 * gesture hook (pan/pinch) AND the per-page Fabric editor, so the two can never
 * disagree:
 *
 *   • pen           → inks (its barrel/eraser end erases) — always, uncapturable
 *   • one finger    → pans — always, including the session's FIRST contact,
 *                     EXCEPT with the eraser explicitly selected (see below)
 *   • two fingers   → pinch-zoom + pan
 *   • mouse         → drives the active tool (desktop)
 *
 * Finger DRAWING is an explicit opt-in. It is persisted (inverted) as
 * `palmReject` in the annotation-tool store — palm rejection ON (the default)
 * means the hand navigates and only the pen inks, i.e. finger-draw is OFF. The
 * stored field name is kept for storage compatibility; everything here reads it
 * as what it now is: finger-draw.
 *
 * Framework-free (the editor imports it): live state is read straight from the
 * store + the shared stylus history, never from React. */

/** Has the user opted into drawing with a finger (palm rejection turned off)? */
export function fingerDrawEnabled(): boolean {
  return !useAnnotationTool.getState().palmReject;
}

/** Is the ERASER the selected tool?
 *
 * The eraser is the one tool a finger may drive without opting into
 * finger-drawing, and the reason is intent. The pen is armed by merely OPENING
 * a note, so a finger touch while the pen is up is far more likely to be a
 * scroll than a stroke — which is exactly why the default is "finger pans".
 * Reaching for the eraser is nobody's accident: it is a deliberate, specific
 * choice of a destructive tool, and the only thing a person then wants to do
 * with a contact on the paper is erase with it.
 *
 * Before this, a tablet user with no stylus in hand selected the eraser, swiped,
 * and nothing happened at all — the gesture layer claimed the touch as
 * navigation and the editor's own gate refused it. The tool looked broken
 * rather than modal.
 *
 * Panning is not lost: two fingers still pinch and pan, on this tool as on
 * every other. */
export function eraserSelected(): boolean {
  return useAnnotationTool.getState().tool === "eraser";
}

/** A touch landing within the palm window of the last pen contact is almost
 * certainly the writing hand's palm, not a deliberate finger stroke. Only matters
 * in finger-draw mode — otherwise every touch already navigates. */
export function isPalmContact(e: PointerEvent): boolean {
  return e.pointerType === "touch" && hasSeenPen() && msSincePen() < PALM_WINDOW_MS;
}

/** Should a lone finger DRAW (be left to the editor) rather than navigate? Only in
 * edit mode with finger-draw opted in — so by default a finger always pans, even
 * the very first contact of a session. The gesture hooks pass this as their
 * `shouldFingerDraw` callback. */
export function fingerDrawActive(editMode: boolean): boolean {
  return editMode && (fingerDrawEnabled() || eraserSelected());
}

/** May THIS touch produce ink/erase inside the editor? Only when finger-draw is
 * opted in AND the contact isn't the writing hand's palm. Pen + mouse are handled
 * separately (never subject to this gate). The editor's touch gate calls this, so
 * a touch it lets through is exactly the touch the gesture hook leaves for it. */
export function touchDrawAllowed(e: PointerEvent): boolean {
  // Palm rejection still applies to the eraser: a palm landing beside a working
  // stylus must not erase what the stylus just wrote, which would be the most
  // destructive possible misread of a contact.
  return (fingerDrawEnabled() || eraserSelected()) && !isPalmContact(e);
}
